import type { ReactNode } from 'react'

import { Icon, Pill, type IconName } from '../ui'
import type { RecordingRecord, SelfReport } from '../../lib/api'
import { formatClockTime, formatDateTime, formatSessionDate } from '../../lib/format'
import { CONDITION_WORDS } from '../../lib/recordings'
import {
  CAFFEINE_LAST_LABELS,
  INTENSITY_LABELS,
  NUMERIC_FIELDS,
  QUALITY_LABELS,
  STATE_OPTIONS,
  TAG_GROUPS,
  TAG_LABELS,
  WHEN_AGO_LABELS,
  hasAnswer,
  numericText,
  type JournalTag,
} from '../../lib/selfReport'

/**
 * History's **Journal** view: the answers a person wrote, read back as a row.
 *
 * Section B of `design-assets/journal-concept/` and owner ruling §5.3 of
 * 2026-10-02: five grouped columns mirroring the form's own groups, each cell
 * one main value and one muted detail line, so a row reads like a sentence
 * rather than a spreadsheet. The ten-column table tried on the way there was
 * withdrawn for reading as one. The columns themselves are wired in
 * `pages/product/History.tsx`, beside the Readings columns they switch with;
 * what lives here is what each cell SAYS.
 *
 * ── THIS FILE COMPUTES NOTHING ──────────────────────────────────────────────
 *
 * Not one number here is added, averaged, counted, rounded or compared with a
 * threshold. Every value is the stored answer put through the formatter the
 * FORM already asks it with — `numericText` (which is `String(n)`), the units
 * in `NUMERIC_FIELDS`, and the four closed label maps. That is criterion 2
 * (`src/test/clientScience.test.ts`), and it is also why there is no second
 * vocabulary: the word a person reads back is the word they answered under.
 *
 * ── ABSENCE IS A DASH, NEVER A ZERO ─────────────────────────────────────────
 *
 * `hasAnswer` is the only test applied to a field, so `0 cups` and
 * `exercised: false` survive while `null` and a key the record never carried
 * read as `—`. The inverse — a truthy test — would show "no caffeine" for a
 * person who wrote 0 and for a person who wrote nothing, which are different
 * facts about the same morning.
 *
 * A schema-1 journal is a journal that was never ASKED three of these
 * questions, so Woke, Finished and Last cup are dashes rather than omissions,
 * and the row carries a "Schema 1" pill: the two must never read alike
 * (concept §B).
 *
 * ── THE ICONS LABEL; THEY NEVER GRADE ───────────────────────────────────────
 *
 * `Icon` is `aria-hidden` by construction, so every icon here sits beside text
 * that already says what it means, and nothing is coloured, arrowed or ranked
 * by its value. The one state an icon carries is `--none`, which DIMS it, and
 * it is set from an answer that literally says none — `caffeine_last: 'none'`
 * and `exercised: false` — never from a number this file looked at.
 */

/** The one absence mark on this screen, as `lib/format.ts` writes it. */
const DASH = '—'

/** What every cell is handed: the row's journal, or the absence of one. */
type Journal = SelfReport | null | undefined

/**
 * A closed-`Record` lookup types as `string` and answers `undefined` off-enum,
 * so the raw wire value is shown rather than nothing — the rule the tag list
 * below already follows, and `JournalSummary`'s too.
 */
const labelled = (label: string | undefined, raw: unknown): string => label ?? String(raw)

/**
 * `TAG_GROUPS[0]` is the form's own "Last night" group, and it is read from
 * there rather than relisted: the split between what a tag says about last
 * night and what it says about this morning is the vocabulary's, and a second
 * list here would be a second answer to the same question.
 */
const LAST_NIGHT_TAGS: readonly string[] = TAG_GROUPS[0]?.tags ?? []

/** The tags of one group, in the vocabulary's own canonical order. */
function tagsOf(report: SelfReport, group: 'last_night' | 'rest'): string[] {
  const tags = report.tags ?? []
  return group === 'last_night'
    ? tags.filter((tag) => LAST_NIGHT_TAGS.includes(tag))
    // Everything else, INCLUDING a tag this build does not know: it is the
    // person's own answer and is shown raw, the rule `JournalSummary`'s list
    // already follows. Dropping it would be the client editing a journal.
    : tags.filter((tag) => !LAST_NIGHT_TAGS.includes(tag))
}

/** One phrase: a label icon, then the text that carries the meaning. */
function Phrase({
  icon,
  none = false,
  children,
}: {
  icon: IconName
  /** The answer was "none". Dims the ICON only — never a colour, never a tone. */
  none?: boolean
  children: ReactNode
}) {
  return (
    <span className="journal-cell__phrase">
      <Icon
        name={icon}
        className={['journal-cell__icon', none ? 'journal-cell__icon--none' : null]
          .filter(Boolean)
          .join(' ')}
      />
      {children}
    </span>
  )
}

/**
 * The two-line cell: one main value, one muted detail.
 *
 * `main` is never allowed to be empty — a blank cell beside five full ones
 * reads as a measured absence rather than as an unanswered question, which is
 * the trap `ConfidenceCell` records one column over.
 */
function Cell({ main, detail }: { main: ReactNode; detail?: ReactNode }) {
  return (
    // `<div>`s and not `<span>`s: a cell's value can BE a list (the tag chips),
    // and a `<ul>` inside a `<span>` is not valid HTML. `<td>` and `<dd>` --
    // the two places `DataTable` renders this -- both take flow content.
    <div className="journal-cell">
      <div className="journal-cell__main">
        {main ?? <span className="history__none">{DASH}</span>}
      </div>
      {detail !== undefined && detail !== null && (
        <div className="journal-cell__detail">{detail}</div>
      )}
    </div>
  )
}

/**
 * The chips, under the vocabulary's own `tag` glyph.
 *
 * A `<div>` wrapper and not `Phrase`: the value this icon labels IS a `<ul>`,
 * and a list inside a `<span>` is not valid HTML -- the same rule `Cell` above
 * records. The classes are `Phrase`'s, so the glyph is the same 14px muted
 * label beside the same 13px text.
 *
 * It labels BOTH tag lists -- the Tags column and the "last night" tags in the
 * evening column -- because a tag is a tag in either place, and the icon says
 * what the chips ARE rather than which column they are in.
 */
function TagList({ tags }: { tags: string[] }) {
  return (
    <div className="journal-cell__phrase">
      <Icon name="tag" className="journal-cell__icon" />
      <ul className="journal-cell__tags">
        {tags.map((tag) => (
          <li className="tag" key={tag}>
            {TAG_LABELS[tag as JournalTag] ?? tag}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A column header with the form's own time anchor under it.
 *
 * The anchor is the whole point of schema 2: "Exercised" on its own does not
 * say whether the exercise was before this recording, and the form asks it
 * under "Today, before this recording · since waking". The table repeats those
 * words rather than paraphrasing them.
 */
export function ColumnHead({ title, anchor }: { title: string; anchor: string }) {
  return (
    <span className="journal-head">
      {title} <small className="journal-head__anchor">{anchor}</small>
    </span>
  )
}

/** The recording this journal belongs to: its date, its time, its condition. */
export function JournalDateCell({ record }: { record: RecordingRecord }) {
  return (
    <span className="history__date">
      {/* The upload time travels with the row here for the same reason it does
          in the Readings view: the list is SORTED by `created_at` while the
          column shows `session_date`. */}
      <span
        className="mono-cell mono-cell--strong"
        title={`Uploaded ${formatDateTime(record.created_at)}`}
      >
        {formatSessionDate(record.session_date)}
      </span>
      <span className="history__detail">
        <span className="mono-cell">{formatClockTime(record.acquisition_started_at)}</span>
        {' · '}
        <span className="mono-cell">{record.condition}</span>
        {/* The code is what the design draws and what an operator reads; the
            words are what a screen reader says. */}
        <span className="visually-hidden"> {CONDITION_WORDS[record.condition]}</span>
      </span>
    </span>
  )
}

/**
 * "Journal skipped", and it does NOT SPAN THE COLUMNS.
 *
 * `DataTable` emits one `<td>` per column for a data row by design (its own
 * comment says why: `columns` is the contract for a record, and a cell that
 * swallowed its neighbours would break the header/cell binding every row
 * depends on). So the label lives in the FIRST group column — this one — and
 * every other journal cell renders nothing at all. The concept draws a
 * `colspan` here; the table's semantics win, and the deviation is recorded in
 * the sprint LOG.
 */
export function FeltCell({ report }: { report: Journal }) {
  if (report == null) {
    return (
      <div className="journal-cell journal-cell--skipped">
        <div className="journal-cell__main">Journal skipped</div>
        <div className="journal-cell__detail">nothing was filed for this recording</div>
      </div>
    )
  }
  const mood = hasAnswer(report.state)
    ? labelled(STATE_OPTIONS.find((o) => o.value === report.state)?.label, report.state)
    : null
  return (
    <Cell
      main={mood}
      detail={
        hasAnswer(report.stress_1_10)
          ? <Phrase icon="activity">stress {numericText(report.stress_1_10)}</Phrase>
          : null
      }
    />
  )
}

export function SleepCell({ report }: { report: Journal }) {
  if (report == null) return null
  const asked =
    hasAnswer(report.sleep_hours)
    || hasAnswer(report.sleep_quality)
    || hasAnswer(report.woke_at_local)
  if (!asked) return <Cell main={null} />
  return (
    <Cell
      main={
        hasAnswer(report.sleep_hours)
          ? (
            <Phrase icon="moon">
              {numericText(report.sleep_hours)} {NUMERIC_FIELDS.sleep_hours.unit}
            </Phrase>
          )
          : null
      }
      detail={
        <Phrase icon="sun">
          {/* A dash and not an omission: a schema-1 journal was never asked the
              wake time, and "7 hours · OK" would read as though it had been and
              left blank. */}
          woke {report.woke_at_local ?? DASH}
          {hasAnswer(report.sleep_quality) && (
            <> · {labelled(QUALITY_LABELS[report.sleep_quality!], report.sleep_quality)}</>
          )}
        </Phrase>
      }
    />
  )
}

export function BeforeCell({ report }: { report: Journal }) {
  if (report == null) return null
  const cups = hasAnswer(report.caffeine_cups)
  const exercised = hasAnswer(report.exercised)
  const effort = hasAnswer(report.exercise_intensity) || hasAnswer(report.exercise_minutes)
  if (!cups && !exercised && !effort) return <Cell main={null} />
  return (
    <Cell
      main={
        cups ? (
          // Dim when the answer SAYS none. `caffeine_last: 'none'` is the
          // form's own "None yet" chip -- not a number this cell read.
          <Phrase icon="coffee" none={report.caffeine_last === 'none'}>
            {numericText(report.caffeine_cups)} {NUMERIC_FIELDS.caffeine_cups.unit}
            {' · last cup '}
            {hasAnswer(report.caffeine_last)
              ? labelled(CAFFEINE_LAST_LABELS[report.caffeine_last!], report.caffeine_last)
              : DASH}
          </Phrase>
        ) : null
      }
      detail={
        exercised || effort ? (
          <Phrase icon="dumbbell" none={report.exercised === false}>
            {exercised && <>Exercised: {report.exercised === true ? 'Yes' : 'No'}</>}
            {/* Finished is asked only of an exercise that happened, and the
                form locks it when Exercised is No (JOURNAL-FIX-01), so a dash
                is owed in exactly one case: it was asked and is not there,
                which is every schema-1 journal. */}
            {report.exercised === true && (
              <> · finished {hasAnswer(report.exercise_finished)
                ? labelled(WHEN_AGO_LABELS[report.exercise_finished!], report.exercise_finished)
                : DASH}</>
            )}
            {hasAnswer(report.exercise_intensity) && (
              <> · {labelled(INTENSITY_LABELS[report.exercise_intensity!], report.exercise_intensity)}</>
            )}
            {hasAnswer(report.exercise_minutes) && (
              <> · {numericText(report.exercise_minutes)} {NUMERIC_FIELDS.exercise_minutes.unit}</>
            )}
          </Phrase>
        ) : null
      }
    />
  )
}

export function EveningCell({ report }: { report: Journal }) {
  if (report == null) return null
  const lastNight = tagsOf(report, 'last_night')
  const drinks = hasAnswer(report.alcohol_drinks)
  if (!drinks && lastNight.length === 0) return <Cell main={null} />
  return (
    <Cell
      main={
        drinks ? (
          <Phrase icon="wine">
            {numericText(report.alcohol_drinks)} {NUMERIC_FIELDS.alcohol_drinks.unit}
          </Phrase>
        ) : null
      }
      // The three "last night" tags belong to the question this column asks
      // (`TAG_GROUPS[0]`); the rest are today's and are in Tags.
      detail={lastNight.length === 0 ? null : <TagList tags={lastNight} />}
    />
  )
}

export function TagsCell({ report }: { report: Journal }) {
  if (report == null) return null
  const tags = tagsOf(report, 'rest')
  const note = report.notes
  const hasNote = note !== null && note !== undefined && note.trim() !== ''
  if (tags.length === 0 && !hasNote) return <Cell main={null} />
  return (
    <Cell
      main={tags.length === 0 ? null : <TagList tags={tags} />}
      // Shown with its own spacing intact: `selfReport.ts` stores the one
      // free-text field exactly as typed, and `trim()` is the test here and
      // never the value.
      detail={hasNote ? <Phrase icon="pen">{note}</Phrase> : null}
    />
  )
}

/** What is true ABOUT this journal rather than in it (concept §B). */
export function JournalPills({ record }: { record: RecordingRecord }) {
  const report = record.self_report
  if (report == null) return null
  // `edited: null` is UNKNOWN and gets no pill: every row written before
  // Sprint 1.9 is in that state, and an "Edited" mark on one would be a claim
  // the server did not make. The word itself is `provenanceWords`'.
  const edited = record.self_report_provenance?.edited === true
  const schemaOne = report.schema_version === 1
  if (!edited && !schemaOne) return null
  return (
    <span className="journal-cell__pills">
      {edited && <Pill tone="amber">Edited</Pill>}
      {/* Not "Older journal": the pill names the schema a reader can look up,
          and it is the reason three cells in this row are dashes. */}
      {schemaOne && <Pill tone="neutral">Schema 1</Pill>}
    </span>
  )
}
